import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Prompt, useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";
import {
  Button,
  Flex,
  FullscreenTakeover,
  LoadingIndicator,
  Step,
  StepIndicator,
  toast,
} from "@jsq/ds/core";
import { DiscardChangesModal } from "~/components/DiscardChangesModal";
import { useGetArenaQuery } from "~/graphql/mainHooks";
import { ReportTemplateVersionStatus } from "~/graphql/supergraphEnums";
import {
  useGetDataPackagesQuery,
  useGetReportTemplateQuery,
  useGetReportTemplatesByArenaQuery,
} from "~/graphql/supergraphHooks";
import { getErrorMessage } from "~/utils/errors";
import { toGlobalId } from "~/utils/graphql-helpers";
import { ReportingMissingPermissionState } from "../components/MissingPermissionState";
import { isUnauthorizedError } from "../utils";
import { ConfirmPublishModal } from "./components/ConfirmPublishModal";
import {
  DuplicateTemplateStep,
  type DuplicateTemplatePrefillData,
} from "./components/DuplicateTemplateStep";
import {
  GeneralSettingsFormValues,
  GeneralSettingsStep,
} from "./components/GeneralSettingsStep";
import { PreviewPublishStep, type PreviewFormValues } from "./components/PreviewPublishStep";
import { PublishSuccessStep } from "./components/PublishSuccessStep";
import { TemplateCreation, TemplateFileData } from "./components/TemplateCreation";
import { TemplateReferenceDrawer } from "./components/TemplateReferenceDrawer";
import {
  UpdateSettingsStep,
  type UpdateSettingsFormValues,
} from "./components/UpdateSettingsStep";
import { usePublishTemplate } from "./hooks/usePublishTemplate";
import * as styles from "./styles_vanilla.css";
import {
  getLatestVersion,
  requireReportTypeEnum,
  toSupportedTemplateType,
  type DrawerMode,
  type DrawerTab,
  type ReportType,
} from "./types";
import type { QueryPreviewContext } from "./components/FormQuerySelectSection";

const ALLOWED_FILE_TYPES = ["docx"];

type ReportTemplateEditorParams = {
  arenaId?: string;
  templateId?: string;
};

const STEPS = {
  SELECT_TEMPLATE: "select-template",
  TEMPLATE_SETTINGS: "template-settings",
  UPDATE_SETTINGS: "update-settings",
  TEMPLATE_CREATION: "template-creation",
  PREVIEW: "preview",
} as const;

type StepName = (typeof STEPS)[keyof typeof STEPS];

type StepConfig = { id: StepName; name: string };

const getStepConfig = (
  isDuplicateRoute: boolean,
  isSystemTemplate: boolean,
  isUpdateMode: boolean,
): StepConfig[] => {
  const previewName = isSystemTemplate ? "Preview" : "Preview and publish";
  if (isUpdateMode) {
    return [
      { id: STEPS.UPDATE_SETTINGS, name: "Update settings" },
      { id: STEPS.TEMPLATE_CREATION, name: "Template creation" },
      { id: STEPS.PREVIEW, name: previewName },
    ];
  }
  if (isDuplicateRoute) {
    return [
      { id: STEPS.SELECT_TEMPLATE, name: "Select template" },
      { id: STEPS.TEMPLATE_SETTINGS, name: "Template settings" },
      { id: STEPS.TEMPLATE_CREATION, name: "Template creation" },
      { id: STEPS.PREVIEW, name: previewName },
    ];
  }
  return [
    { id: STEPS.TEMPLATE_SETTINGS, name: "Template settings" },
    { id: STEPS.TEMPLATE_CREATION, name: "Template creation" },
    { id: STEPS.PREVIEW, name: previewName },
  ];
};

function getHeaderTitle({
  isNewTemplate,
  isSystemTemplate,
  isDuplicateRoute,
  isUpdateMode,
  existingName,
  templateId,
}: {
  isNewTemplate: boolean;
  isSystemTemplate: boolean;
  isDuplicateRoute: boolean;
  isUpdateMode: boolean;
  existingName?: string;
  templateId?: string;
}): string {
  if (isDuplicateRoute) return "Duplicate template";
  if (isUpdateMode) return "Update template";
  if (isNewTemplate && isSystemTemplate) return "New system template";
  if (isNewTemplate) return "New template";
  return existingName ?? `Template ${templateId}`;
}

export const ReportTemplateEditor = () => {
  const { arenaId, templateId } = useParams<ReportTemplateEditorParams>();
  const history = useHistory();
  const location = useLocation();
  const isArenaDuplicateRoute = !!useRouteMatch("/reporting/:arenaId/templates/duplicate");
  const isSystemDuplicateRoute = !!useRouteMatch("/reporting/system/templates/duplicate");
  const isDuplicateRoute = isArenaDuplicateRoute || isSystemDuplicateRoute;

  const initialDuplicateTemplateId = useMemo(() => {
    if (!isDuplicateRoute) return null;
    const raw = new URLSearchParams(location.search).get("templateId");
    if (!raw) return null;
    const id = Number(raw);
    return Number.isNaN(id) || id === 0 ? null : id;
  }, [isDuplicateRoute, location.search]);

  const isNewTemplate = !templateId;
  const isSystemTemplate = arenaId == null || arenaId === "system";

  const templateIdNum = templateId ? Number(templateId) : null;
  const arenaIdNum = isSystemTemplate ? null : Number(arenaId);
  const isInvalidArenaId =
    !isSystemTemplate && (arenaIdNum === null || Number.isNaN(arenaIdNum));

  const shouldSkipQuery = isNewTemplate || !templateIdNum || Number.isNaN(templateIdNum);
  const { data, loading } = useGetReportTemplateQuery({
    variables: { id: templateIdNum ?? 0 },
    skip: shouldSkipQuery,
    fetchPolicy: "cache-and-network",
  });

  const effectiveVersion = useMemo(
    () =>
      getLatestVersion(data?.reportTemplate?.currentVersion, data?.reportTemplate?.versions),
    [data?.reportTemplate?.currentVersion, data?.reportTemplate?.versions],
  );

  const isUpdateMode = useMemo(() => {
    if (isNewTemplate || isDuplicateRoute || !data?.reportTemplate) return false;
    const status = effectiveVersion?.status;
    const isArchived = data.reportTemplate.archivedAt != null;
    return (
      !isArchived &&
      (status === ReportTemplateVersionStatus.Pending ||
        status === ReportTemplateVersionStatus.Denied)
    );
  }, [isNewTemplate, isDuplicateRoute, data?.reportTemplate, effectiveVersion?.status]);

  const stepConfig = useMemo(
    () => getStepConfig(isDuplicateRoute, isSystemTemplate, isUpdateMode),
    [isDuplicateRoute, isSystemTemplate, isUpdateMode],
  );
  const firstStepId = stepConfig[0]?.id ?? STEPS.TEMPLATE_SETTINGS;

  const isValidStep = useCallback(
    (step: string | null): step is StepName =>
      step !== null && stepConfig.some((s) => s.id === step),
    [stepConfig],
  );

  const getStepFromUrl = useCallback((): StepName => {
    if (isNewTemplate) {
      return firstStepId;
    }
    const searchParams = new URLSearchParams(location.search);
    const stepFromUrl = searchParams.get("step");
    return isValidStep(stepFromUrl) ? stepFromUrl : firstStepId;
  }, [location.search, isNewTemplate, firstStepId, isValidStep]);

  const [currentStep, setCurrentStep] = useState<StepName>(getStepFromUrl);

  useEffect(() => {
    if (isUpdateMode && !stepConfig.some((s) => s.id === currentStep)) {
      setCurrentStep(firstStepId);
      history.replace(`${location.pathname}?step=${firstStepId}`);
    }
  }, [isUpdateMode, stepConfig, currentStep, firstStepId, history, location.pathname]);

  /**
   * One-time initialization guard for URL cleanup on new templates.
   *
   * Problem: New templates should always start at step 1, but users might bookmark
   * or manually navigate to URLs like `/templates/new?step=preview`.
   *
   * Solution: On initial mount, strip invalid step params from the URL.
   * The ref ensures this only runs once - without it, the effect would re-run
   * every time handleNext/handleBack update the URL query params.
   */
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    const searchParams = new URLSearchParams(location.search);
    const stepFromUrl = searchParams.get("step");

    if (isNewTemplate && stepFromUrl && stepFromUrl !== firstStepId) {
      history.replace(location.pathname);
    }
  }, [isNewTemplate, location.pathname, location.search, history, firstStepId]);

  const [generalSettings, setGeneralSettings] = useState<GeneralSettingsFormValues>({
    templateName: "",
    templateType: "",
    dataPackage: "",
    query: "",
  });
  // Tracks the live template-type selection from GeneralSettingsStep so the
  // data-packages query can filter by templateType before the form is submitted.
  const [templateTypeFilter, setTemplateTypeFilter] = useState<ReportType | "">("");
  const [updateSettings, setUpdateSettings] = useState<UpdateSettingsFormValues>({
    dataPackage: "",
    query: "",
  });
  const [generalSettingsKey, setGeneralSettingsKey] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeDrawerTab, setActiveDrawerTab] = useState<DrawerTab>("instructions");
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("full");
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  /**
   * Holds template file data from Carbone Studio until publish.
   * This state persists across step navigation because we keep the TemplateCreation
   * component mounted (hidden with CSS) rather than unmounting it.
   */
  const [pendingTemplateFile, setPendingTemplateFile] = useState<TemplateFileData | null>(null);
  const [previewFormValues, setPreviewFormValues] = useState<PreviewFormValues | null>(null);
  const [isPublished, setIsPublished] = useState(false);

  /**
   * Distinguishes intentional navigation (save/publish) from accidental navigation.
   * When isIntentionalExit is true, the unsaved changes prompt (react-router's <Prompt>) is bypassed.
   * We use a ref instead of state because we need to set it synchronously before
   * navigation occurs, without triggering a re-render.
   */
  const isIntentionalExit = useRef(false);

  const { data: arenaData } = useGetArenaQuery({
    variables: { id: toGlobalId("ArenaObjectType", arenaIdNum ?? 0) },
    skip: isInvalidArenaId || isSystemTemplate,
  });

  const arenaName = isSystemTemplate ? "System" : (arenaData?.arena?.name ?? "");
  const arenaGlobalId =
    isSystemTemplate || isInvalidArenaId ? "" : toGlobalId("ArenaObjectType", arenaIdNum ?? 0);

  const {
    data: dataPackagesData,
    previousData: previousDataPackagesData,
    loading: dataPackagesLoading,
    error: dataPackagesError,
  } = useGetDataPackagesQuery({
    variables: {
      arenaId: arenaIdNum,
      includeInactive: false,
      templateType: toSupportedTemplateType(templateTypeFilter) ?? undefined,
    },
    skip: isInvalidArenaId,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: templatesData,
    loading: templatesLoading,
    error: templatesError,
  } = useGetReportTemplatesByArenaQuery({
    variables: { arenaId: isSystemTemplate ? null : arenaIdNum },
    skip: isInvalidArenaId,
  });

  const existingTemplateNames = useMemo(
    () => new Set((templatesData?.reportTemplatesByArena ?? []).map((t) => t.name)),
    [templatesData],
  );
  const dataPackageOptions = useMemo(() => {
    return (dataPackagesData?.finpubDataPackages ?? [])
      .filter((pkg) => pkg.isActive)
      .map((pkg) => ({
        id: pkg.id,
        name: pkg.name,
        arenaId: pkg.arenaId ?? null,
        isSystem: pkg.arenaId == null,
        currentVersionId: pkg.currentVersionId ?? null,
      }));
  }, [dataPackagesData]);

  const committedSettingsDataPackageId = isUpdateMode
    ? updateSettings.dataPackage
    : generalSettings.dataPackage;

  const committedPackageArenaId = useMemo(() => {
    if (!committedSettingsDataPackageId) return null;
    const pkg = dataPackageOptions.find(
      (option) => String(option.id) === committedSettingsDataPackageId,
    );
    return pkg?.arenaId ?? null;
  }, [committedSettingsDataPackageId, dataPackageOptions]);

  const [drawerQueryContext, setDrawerQueryContext] = useState<QueryPreviewContext | null>(
    null,
  );

  const committedQueryId = useMemo(() => {
    const raw = isUpdateMode ? updateSettings.query : generalSettings.query;
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }, [generalSettings.query, isUpdateMode, updateSettings.query]);

  const drawerQueryId = drawerQueryContext?.queryId ?? committedQueryId;
  const drawerArenaId = drawerQueryContext?.arenaId ?? committedPackageArenaId;

  const drawerDataPackageIdRaw =
    drawerQueryContext?.dataPackageId ?? committedSettingsDataPackageId;

  const drawerDataPackageId = useMemo(() => {
    if (!drawerDataPackageIdRaw) return null;
    const parsed = Number(drawerDataPackageIdRaw);
    return Number.isNaN(parsed) ? null : parsed;
  }, [drawerDataPackageIdRaw]);

  const selectedPackageArenaId = drawerQueryContext?.arenaId ?? committedPackageArenaId;

  const dataStructureBasePath =
    selectedPackageArenaId == null
      ? "/reporting/settings/data-structures"
      : `/reporting/${selectedPackageArenaId}/data-structures`;

  const dataPackageName = useMemo(() => {
    if (!generalSettings.dataPackage) {
      return "";
    }
    const pkg = dataPackageOptions.find((p) => String(p.id) === generalSettings.dataPackage);
    return pkg?.name ?? "";
  }, [generalSettings.dataPackage, dataPackageOptions]);

  const updateModeSupportedTemplateType = useMemo(() => {
    const reportType = data?.reportTemplate?.reportType;
    if (!reportType) return undefined;
    return toSupportedTemplateType(reportType);
  }, [data?.reportTemplate?.reportType]);

  const navigateToArenaDetail = useCallback(() => {
    if (isSystemTemplate) {
      history.push("/reporting/settings");
    } else {
      history.push(`/reporting/${arenaId}`);
    }
  }, [history, arenaId, isSystemTemplate]);

  const hasRedirectedForEmptyTemplates = useRef(false);
  useEffect(() => {
    if (
      !hasRedirectedForEmptyTemplates.current &&
      isDuplicateRoute &&
      !templatesLoading &&
      !templatesError &&
      (templatesData?.reportTemplatesByArena ?? []).length === 0
    ) {
      hasRedirectedForEmptyTemplates.current = true;
      navigateToArenaDetail();
    }
  }, [
    isDuplicateRoute,
    templatesLoading,
    templatesError,
    templatesData,
    navigateToArenaDetail,
  ]);

  const templateArenaId = data?.reportTemplate?.arenaId;
  const isWrongArena =
    !isInvalidArenaId && templateArenaId !== undefined && templateArenaId !== arenaIdNum;

  // Store original values loaded from API to compare for unsaved changes detection
  const [originalSettings, setOriginalSettings] = useState<GeneralSettingsFormValues | null>(
    null,
  );
  const seededTemplateIdRef = useRef<number | null>(null);

  const { handlePublish, updateTemplate, isSaving } = usePublishTemplate({
    isNewTemplate,
    isUpdateMode,
    isSystemTemplate,
    templateIdNum,
    templateName: data?.reportTemplate?.name ?? null,
    arenaIdNum,
    generalSettings,
    originalSettings,
    dataPackageName,
    dataPackageOptions,
    updateSettings,
    pendingTemplateFile,
    onPublishSuccess: () => {
      isIntentionalExit.current = true;
      setIsPublished(true);
    },
  });

  useEffect(() => {
    if (isInvalidArenaId) {
      toast.error("Invalid arena ID");
      history.push("/reporting");
      return;
    }

    if (isWrongArena) {
      toast.error("Template not found in this arena");
      navigateToArenaDetail();
      return;
    }

    const template = data?.reportTemplate;
    if (!template) return;

    if (seededTemplateIdRef.current === template.id) return;
    seededTemplateIdRef.current = template.id;

    // Status guard: block update flow for approved or archived templates
    if (!isNewTemplate && !isDuplicateRoute) {
      const versionStatus = effectiveVersion?.status;
      const isArchived = template.archivedAt != null;
      if (isArchived) {
        toast.error("Archived templates cannot be updated");
        navigateToArenaDetail();
        return;
      }
      if (versionStatus === ReportTemplateVersionStatus.Approved) {
        toast.error("Approved templates can only have their name updated");
        navigateToArenaDetail();
        return;
      }
    }

    const loadedVersion = getLatestVersion(template.currentVersion, template.versions);
    const loadedQueryId = loadedVersion?.queryId != null ? String(loadedVersion.queryId) : "";

    const loadedSettings: GeneralSettingsFormValues = {
      templateName: template.name,
      templateType: template.reportType,
      dataPackage: template.dataPackage != null ? String(template.dataPackage.id) : "",
      query: loadedQueryId,
    };
    setGeneralSettings(loadedSettings);
    setOriginalSettings(loadedSettings);
    setTemplateTypeFilter(loadedSettings.templateType);

    setUpdateSettings({
      dataPackage: template.dataPackage != null ? String(template.dataPackage.id) : "",
      query: loadedQueryId,
    });
  }, [
    data,
    isWrongArena,
    isInvalidArenaId,
    isNewTemplate,
    isDuplicateRoute,
    effectiveVersion?.status,
    navigateToArenaDetail,
    history,
  ]);

  const currentStepIndex = stepConfig.findIndex((s) => s.id === currentStep);

  useEffect(() => {
    return history.listen((newLocation, action) => {
      if (action !== "POP") return;
      const step = new URLSearchParams(newLocation.search).get("step");
      setCurrentStep(isValidStep(step) ? step : firstStepId);
    });
  }, [history, isValidStep, firstStepId]);

  const hasUnsavedChanges = useMemo(() => {
    if (currentStepIndex > 0 || isFormDirty) {
      return true;
    }

    // For new templates, check if any field has been filled
    if (isNewTemplate) {
      return (
        generalSettings.templateName !== "" ||
        generalSettings.templateType !== "" ||
        generalSettings.dataPackage !== "" ||
        generalSettings.query !== ""
      );
    }

    // For existing templates, compare against original loaded values
    if (!originalSettings) {
      return false;
    }

    return (
      generalSettings.templateName !== originalSettings.templateName ||
      generalSettings.templateType !== originalSettings.templateType ||
      generalSettings.dataPackage !== originalSettings.dataPackage ||
      generalSettings.query !== originalSettings.query
    );
  }, [currentStepIndex, isFormDirty, isNewTemplate, generalSettings, originalSettings]);

  const shouldWarnOnLeave =
    !isPublished && (hasUnsavedChanges || isNewTemplate || isUpdateMode);

  useEffect(() => {
    if (!shouldWarnOnLeave) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldWarnOnLeave]);

  const handleExit = useCallback(() => {
    if (isPublished) {
      isIntentionalExit.current = true;
      navigateToArenaDetail();
      return;
    }
    if (hasUnsavedChanges || isNewTemplate || isUpdateMode) {
      setShowDiscardModal(true);
      return;
    }
    navigateToArenaDetail();
  }, [isPublished, hasUnsavedChanges, isNewTemplate, isUpdateMode, navigateToArenaDetail]);

  const handleDiscard = useCallback(() => {
    setShowDiscardModal(false);
    setPendingTemplateFile(null);
    isIntentionalExit.current = true;
    navigateToArenaDetail();
  }, [navigateToArenaDetail]);

  const handleSaveAndExit = useCallback(async () => {
    if (!templateIdNum) {
      return;
    }

    try {
      const reportTypeUpdate =
        originalSettings &&
        generalSettings.templateType !== originalSettings.templateType &&
        generalSettings.templateType !== ""
          ? requireReportTypeEnum(generalSettings.templateType)
          : undefined;
      await updateTemplate({
        variables: {
          id: templateIdNum,
          input: {
            name: generalSettings.templateName,
            ...(reportTypeUpdate ? { reportType: reportTypeUpdate } : {}),
          },
        },
      });
      toast.success("Template saved successfully");
      isIntentionalExit.current = true;
      navigateToArenaDetail();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [
    updateTemplate,
    templateIdNum,
    generalSettings.templateName,
    generalSettings.templateType,
    originalSettings,
    navigateToArenaDetail,
  ]);

  const handleOpenDrawer = useCallback((tab: DrawerTab, mode: DrawerMode = "full") => {
    setActiveDrawerTab(tab);
    setDrawerMode(mode);
    setIsDrawerOpen(true);
  }, []);

  // Push step to URL query params for browser history support.
  // This allows back/forward navigation between steps.
  const handleNext = useCallback(() => {
    if (currentStepIndex < stepConfig.length - 1) {
      const nextStep = stepConfig[currentStepIndex + 1];
      if (nextStep) {
        history.push(`${location.pathname}?step=${nextStep.id}`);
        setCurrentStep(nextStep.id);
        if (nextStep.id === STEPS.TEMPLATE_CREATION && !isUpdateMode) {
          handleOpenDrawer("instructions");
        }
      }
    }
  }, [
    currentStepIndex,
    stepConfig,
    history,
    location.pathname,
    handleOpenDrawer,
    isUpdateMode,
  ]);

  const handleSelectTemplateNext = useCallback(
    (fileData: TemplateFileData | null, prefillData: DuplicateTemplatePrefillData) => {
      if (fileData) {
        setPendingTemplateFile(fileData);
      }
      setGeneralSettings((prev) => ({
        ...prev,
        templateType: prefillData.templateType,
        dataPackage: prefillData.dataPackageId,
        query: prefillData.queryId,
      }));
      setTemplateTypeFilter(prefillData.templateType);
      setGeneralSettingsKey((k) => k + 1);
      handleNext();
    },
    [handleNext],
  );

  const handleGeneralSettingsSubmit = useCallback(
    (values: GeneralSettingsFormValues) => {
      setGeneralSettings(values);
      handleNext();
    },
    [handleNext],
  );

  const handleTemplateTypeChange = useCallback((type: ReportType | "") => {
    setTemplateTypeFilter(type);
  }, []);

  const handleUpdateSettingsSubmit = useCallback(
    (values: UpdateSettingsFormValues) => {
      setUpdateSettings(values);
      handleNext();
    },
    [handleNext],
  );

  const handleTemplateChange = useCallback((data: TemplateFileData) => {
    if (!ALLOWED_FILE_TYPES.includes(data.fileType.toLowerCase())) {
      toast.error(`Unsupported file type: ${data.fileType}. Only DOCX files are supported.`);
      return;
    }
    setPendingTemplateFile(data);
  }, []);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevStep = stepConfig[currentStepIndex - 1];
      if (prevStep) {
        history.push(`${location.pathname}?step=${prevStep.id}`);
        setCurrentStep(prevStep.id);
      }
    }
  }, [currentStepIndex, stepConfig, history, location.pathname]);

  const handlePreviewFieldsFromForm = useCallback(
    (context: QueryPreviewContext) => {
      setDrawerQueryContext(context);
      handleOpenDrawer("query-reference", "query-only");
    },
    [handleOpenDrawer],
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setDrawerQueryContext(null);
  }, []);

  const handleOpenPublishModal = useCallback(() => {
    setIsPublishModalOpen(true);
  }, []);

  const handleClosePublishModal = useCallback(() => {
    setIsPublishModalOpen(false);
  }, []);

  const steps: Step[] = useMemo(
    () =>
      stepConfig.map((step, index) => {
        let status: "active" | "inactive" | "complete" = "inactive";
        if (isPublished) {
          status = "complete";
        } else if (index < currentStepIndex) {
          status = "complete";
        } else if (index === currentStepIndex) {
          status = "active";
        }
        return {
          name: step.name,
          url: step.id,
          status,
          hidden: false,
          index,
        };
      }),
    [stepConfig, currentStepIndex, isPublished],
  );

  /**
   * Renders all step content with CSS-based visibility instead of conditional mounting.
   *
   * After a successful publish, replaces all step content with a success page that
   * links to the arena's external workspace URL.
   *
   * Why CSS-based visibility: TemplateCreation hosts a complex third-party editor
   * (Carbone Studio) that maintains internal state. Unmounting it when navigating
   * away would lose the user's work-in-progress template.
   *
   * Solution: Keep steps 1-3 always mounted but toggle visibility with CSS.
   * Step 4 (Preview) uses conditional rendering with lifted state for persistence.
   * - `display: contents` makes the wrapper invisible to layout while children render
   * - `display: none` hides inactive steps completely
   */
  const renderStepContent = useCallback(() => {
    if (isPublished) {
      return (
        <PublishSuccessStep
          templateName={generalSettings.templateName}
          arenaName={arenaName}
          arenaId={arenaIdNum}
          isSystemTemplate={isSystemTemplate}
        />
      );
    }

    return (
      <>
        {isUpdateMode && (
          <div
            className={
              currentStep === STEPS.UPDATE_SETTINGS
                ? styles.stepContentVisible
                : styles.stepContentHidden
            }
          >
            <UpdateSettingsStep
              downloadUrl={effectiveVersion?.downloadUrl ?? null}
              initialValues={updateSettings}
              onSubmit={handleUpdateSettingsSubmit}
              dataPackageOptions={dataPackageOptions}
              dataPackagesLoading={dataPackagesLoading}
              viewerArenaId={arenaIdNum}
              arenaName={arenaName}
              onPreviewFields={handlePreviewFieldsFromForm}
              supportedTemplateType={updateModeSupportedTemplateType}
            />
          </div>
        )}
        {isDuplicateRoute && (
          <div
            className={
              currentStep === STEPS.SELECT_TEMPLATE
                ? styles.stepContentVisible
                : styles.stepContentHidden
            }
          >
            <DuplicateTemplateStep
              arenaId={arenaIdNum}
              arenaName={arenaName}
              initialTemplateId={initialDuplicateTemplateId}
              onNext={handleSelectTemplateNext}
            />
          </div>
        )}
        {!isUpdateMode && (
          <div
            className={
              currentStep === STEPS.TEMPLATE_SETTINGS
                ? styles.stepContentVisible
                : styles.stepContentHidden
            }
          >
            <GeneralSettingsStep
              key={generalSettingsKey}
              initialValues={generalSettings}
              existingTemplateNames={existingTemplateNames}
              onSubmit={handleGeneralSettingsSubmit}
              onPreviewFields={handlePreviewFieldsFromForm}
              onDirtyChange={setIsFormDirty}
              onTemplateTypeChange={handleTemplateTypeChange}
              isLoading={isSaving}
              dataPackageOptions={dataPackageOptions}
              dataPackagesLoading={dataPackagesLoading}
              dataPackagesError={!!dataPackagesError}
              viewerArenaId={arenaIdNum}
              arenaName={arenaName}
              onBack={isDuplicateRoute ? handleBack : undefined}
            />
          </div>
        )}
        <div
          className={
            currentStep === STEPS.TEMPLATE_CREATION
              ? styles.stepContentVisible
              : styles.stepContentHidden
          }
        >
          <TemplateCreation
            onTemplateChange={handleTemplateChange}
            isActive={currentStep === STEPS.TEMPLATE_CREATION}
          />
        </div>
        {currentStep === STEPS.PREVIEW && (
          <PreviewPublishStep
            templateData={pendingTemplateFile}
            arenaId={arenaGlobalId}
            arenaName={arenaName}
            initialReportType={generalSettings.templateType}
            dataPackageName={dataPackageName}
            queryId={committedQueryId}
            dataPackageArenaId={committedPackageArenaId}
            templateName={generalSettings.templateName}
            isSystemTemplate={isSystemTemplate}
            savedFormValues={previewFormValues}
            onFormValuesChange={setPreviewFormValues}
          />
        )}
      </>
    );
  }, [
    arenaGlobalId,
    arenaIdNum,
    arenaName,
    committedPackageArenaId,
    committedQueryId,
    currentStep,
    dataPackageName,
    dataPackageOptions,
    dataPackagesLoading,
    effectiveVersion?.downloadUrl,
    generalSettings,
    generalSettingsKey,
    handleBack,
    handleGeneralSettingsSubmit,
    handleTemplateTypeChange,
    handleUpdateSettingsSubmit,
    existingTemplateNames,
    handleOpenDrawer,
    handleSelectTemplateNext,
    handleTemplateChange,
    initialDuplicateTemplateId,
    isDuplicateRoute,
    isPublished,
    isSaving,
    isSystemTemplate,
    isUpdateMode,
    pendingTemplateFile,
    previewFormValues,
    updateModeSupportedTemplateType,
    updateSettings,
  ]);

  const isLastStep = currentStepIndex === stepConfig.length - 1;
  const publishButtonLabel = isSystemTemplate
    ? "Save system template"
    : "Publish to client workspace";

  const showFooter =
    !isPublished && (currentStep === STEPS.TEMPLATE_CREATION || currentStep === STEPS.PREVIEW);

  const needsNoPaddingBody =
    currentStep === STEPS.SELECT_TEMPLATE || (currentStep === STEPS.PREVIEW && !isPublished);

  const headerTitle = getHeaderTitle({
    isNewTemplate,
    isSystemTemplate,
    isDuplicateRoute,
    isUpdateMode,
    existingName: data?.reportTemplate?.name,
    templateId,
  });

  if (!isSystemTemplate && dataPackagesError && isUnauthorizedError(dataPackagesError)) {
    return <ReportingMissingPermissionState />;
  }

  // Only show the fullscreen loader for the initial data-packages fetch.
  // Later refetches (e.g. changing the template type updates the query
  // variables) must not unmount the form and wipe the user's input.
  if (
    loading ||
    (!isSystemTemplate && dataPackagesLoading && !dataPackagesData && !previousDataPackagesData)
  ) {
    return (
      <FullscreenTakeover disablePortal>
        <FullscreenTakeover.Header
          title={
            loading && !isNewTemplate && !isDuplicateRoute ? "Update template" : headerTitle
          }
          subtitle={arenaName || undefined}
          leftActionContent={
            <Button variant="muted" icon="xmark" onClick={navigateToArenaDetail}>
              Cancel
            </Button>
          }
        />
        <FullscreenTakeover.Body>
          <Flex justifyContent="center" alignItems="center" className={styles.loadingCenter}>
            <LoadingIndicator />
          </Flex>
        </FullscreenTakeover.Body>
      </FullscreenTakeover>
    );
  }

  if (!isNewTemplate && !isDuplicateRoute && !data?.reportTemplate) {
    return null;
  }

  return (
    <FullscreenTakeover disablePortal>
      <Prompt
        when={hasUnsavedChanges || isNewTemplate || isUpdateMode}
        message={(location) => {
          if (isIntentionalExit.current || location.pathname === history.location.pathname) {
            return true;
          }
          return "You have unsaved changes. Are you sure you want to leave?";
        }}
      />
      <FullscreenTakeover.Header
        title={headerTitle}
        subtitle={isSystemTemplate ? undefined : arenaName}
        leftActionContent={
          <Button variant="muted" icon="xmark" onClick={handleExit}>
            Exit
          </Button>
        }
        rightActionContent={
          !isNewTemplate && !isUpdateMode && !isPublished ? (
            <Button variant="neutral" onClick={handleSaveAndExit} disabled={isSaving}>
              Save and exit
            </Button>
          ) : undefined
        }
      />
      <FullscreenTakeover.Subheader>
        <StepIndicator steps={steps} disableStepLinks noPadding />
      </FullscreenTakeover.Subheader>
      <FullscreenTakeover.Body
        className={needsNoPaddingBody ? styles.previewPublishBody : undefined}
      >
        {renderStepContent()}
      </FullscreenTakeover.Body>
      {showFooter && (
        <FullscreenTakeover.Footer>
          <Flex justifyContent="space-between" alignItems="center" className={styles.footer}>
            <Flex gap={16}>
              {currentStep === STEPS.TEMPLATE_CREATION && (
                <>
                  <Button
                    variant="muted"
                    icon="info-circle"
                    onClick={() => handleOpenDrawer("instructions")}
                  >
                    Instructions
                  </Button>
                  <Button
                    variant="muted"
                    icon="list-alt"
                    onClick={() => handleOpenDrawer("query-reference")}
                  >
                    Query reference
                  </Button>
                </>
              )}
            </Flex>
            <Flex gap={8}>
              <Button variant="neutral" onClick={handleBack}>
                Back
              </Button>
              {!isLastStep ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={
                    isSaving ||
                    (currentStep === STEPS.TEMPLATE_CREATION &&
                      (!pendingTemplateFile ||
                        !ALLOWED_FILE_TYPES.includes(
                          pendingTemplateFile.fileType.toLowerCase(),
                        )))
                  }
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={
                    isSystemTemplate ? () => void handlePublish() : handleOpenPublishModal
                  }
                  disabled={isSaving}
                >
                  {publishButtonLabel}
                </Button>
              )}
            </Flex>
          </Flex>
        </FullscreenTakeover.Footer>
      )}

      <ConfirmPublishModal
        show={isPublishModalOpen}
        onClose={handleClosePublishModal}
        onConfirm={(shouldAutoApprove: boolean) => {
          handleClosePublishModal();
          void handlePublish(shouldAutoApprove);
        }}
        arenaName={arenaName}
        isSaving={isSaving}
      />

      <DiscardChangesModal
        show={showDiscardModal}
        onKeepEditing={() => setShowDiscardModal(false)}
        onDiscard={handleDiscard}
        title={isUpdateMode ? "Discard update?" : "Discard template?"}
      />

      {(currentStep === STEPS.TEMPLATE_SETTINGS ||
        currentStep === STEPS.UPDATE_SETTINGS ||
        currentStep === STEPS.TEMPLATE_CREATION ||
        currentStep === STEPS.PREVIEW) && (
        <TemplateReferenceDrawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          mode={drawerMode}
          initialTab={activeDrawerTab}
          queryId={drawerQueryId}
          arenaId={drawerArenaId}
          dataPackageId={drawerDataPackageId}
          dataStructureBasePath={dataStructureBasePath}
        />
      )}
    </FullscreenTakeover>
  );
};
